import {cva, VariantProps} from 'class-variance-authority';
import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  forwardRef,
  MouseEventHandler,
  ReactNode,
} from 'react';

import {cn} from 'App/utils/ui';

export type BasicButtonProps = {
  variant?: TButtonVariants['variant'];
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement> | (() => void);
  className?: string;
  size?: TButtonVariants['size'];
  disabled?: boolean;
  loading?: boolean;
  loadingClassName?: string;
};

const Button = forwardRef<
  HTMLButtonElement,
  BasicButtonProps &
    DetailedHTMLProps<
      ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >
>(
  (
    {
      type = 'button',
      onClick,
      className,
      size,
      variant,
      disabled = false,
      loading = false,
      children,
      loadingClassName,
      ...restProps
    },
    ref,
  ) => {
    const styles = buttonVariants({
      variant: disabled ? 'disabled' : variant,
      size,
    });

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        className={cn(styles, className)}
        {...restProps}
      >
        {children}
        {loading && (
          <svg
            className={cn(
              'ml-2 h-[13px] w-[13px] animate-spin',
              loadingClassName,
            )}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </button>
    );
  },
);

const buttonVariants = cva(
  [
    'relative flex items-center rounded-lg border-2 font-medium outline-2 outline-offset-1 focus-visible:outline-solid',
    'outline-seafoam-400',
  ],
  {
    variants: {
      variant: {
        oldPrimary:
          'bg-seafoam-200 border-seafoam-200 text-white hover:brightness-90 active:brightness-75',
        primary:
          'bg-primary-100 hover:bg-primary-dark border-0 text-white active:brightness-75',
        secondary:
          'hover:bg-seafoam-200 hover:border-seafoam-200 outline-seafoam-400 transition-colors hover:text-white active:brightness-75',
        oldTertiary:
          'hover:border-seafoam-200 outline-seafoam-400 transition-colors active:brightness-75',
        tertiary:
          'text-secondary-80 hover:text-secondary-100 h-min border-0 outline-0 focus-visible:outline-0',
        danger:
          'border-red-600 bg-red-600 text-white hover:brightness-90 active:brightness-75',
        'danger-outline':
          'border-0 bg-white text-black transition-colors hover:bg-red-600 hover:text-white active:brightness-75',
        outline:
          'border-seafoam-200 text-seafoam-200 hover:bg-seafoam-200 transition-colors hover:text-white hover:brightness-100 active:brightness-75',
        oldNav:
          'overflow-hidden rounded-xl border-none bg-green-400 p-0 text-white transition-colors hover:brightness-[0.97] active:brightness-95',
        nav: 'bg-gray-light text-secondary-100 text-opacity-60 overflow-hidden rounded-lg border-none p-0 transition-colors hover:brightness-[0.97] active:brightness-95',
        text: 'text-seafoam-200 border-none',
        disabled: 'cursor-not-allowed bg-gray-200 text-gray-800',
        svg: [
          '[&_svg>path]:fill-secondary-100 [&_svg]:fill-secondary-100',
          'hover:[&_svg>path]:fill-secondary-80 hover:[&_svg]:fill-secondary-80',
        ],
      },
      size: {
        small: 'px-3 py-1 text-sm',
        normal: 'text-md px-4 py-2',
        big: 'px-14 py-2 text-lg',
        large: 'px-20 py-4 text-3xl',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'normal',
    },
    compoundVariants: [
      {
        variant: 'svg',
        className: 'text-md w-8 border-none p-0',
      },
      {
        variant: 'tertiary',
      },
    ],
  },
);
type TButtonVariants = VariantProps<typeof buttonVariants>;

export default Button;
